import VectorSource from 'ol/source/Vector';
import {AfterViewInit, signal} from '@angular/core';
import {scaleThreshold} from 'd3-scale';
import {MapLayer} from '../map-layer';
import GeoJSON from 'ol/format/GeoJSON';
import {Feature} from 'ol';
import {Geometry} from 'ol/geom';
import VectorLayer from 'ol/layer/Vector';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import {IceLayerFeature, IceLayerScale, IceService, IceStyleProperty} from '../../../services/ice.service';
import Text from 'ol/style/Text';



export class IceSource extends VectorSource{
  public mapLayer: MapLayer;
  public colors = ['#580000', '#a54d15', '#edc58d', '#ffffe0', '#cce2cf', '#48aaa7', '#02881d'].reverse();
  public legendBounds = signal({ min: Number.MAX_SAFE_INTEGER, max: Number.MIN_SAFE_INTEGER });


  private hatchPattern = (() => {
    const canvas = document.createElement('canvas');
    canvas.width = 12;
    canvas.height = 12;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return undefined;
    }

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.85)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, 12);
    ctx.lineTo(12, 0);
    ctx.stroke();
    return ctx.createPattern(canvas, 'repeat') ?? undefined;
  })();

  constructor(mapLayer: MapLayer) {
    super();
    this.mapLayer = mapLayer;
  }



  processFeatures(features: IceLayerFeature[],
                  scale: IceLayerScale,
                  styleProperty: IceStyleProperty = 'totalCost') {

    const f = new GeoJSON().readFeatures({
      type: 'FeatureCollection',
      features: features.map((feature: IceLayerFeature) => {
        const fp = feature.properties;
        fp['popoverData'] = IceService.getPopoverForFeature(feature);
        return feature;
      }),
    });

    this.legendBounds.set({
      min: scale.min,
      max: scale.max,
    });

    // `toValue` returns normalized values centered around quartiles (q1=0, q3=1),
    // so thresholds must also be in normalized space.
    const domain = [];
    for (let i = 1; i < this.colors.length; i++) {
      domain.push(i / this.colors.length);
    }
    const colorScale = scaleThreshold<number, string>(
      domain,
      this.colors,
    );


    this.mapLayer.renderedLayer = new VectorLayer({
      source: new VectorSource({
        features: f as Feature<Geometry>[],
        attributions: this.mapLayer.buildAttribution(),
      }),
      properties: {
        name: this.mapLayer.displayName,
      },
      opacity: 0.8,
      style: (feature, resolution: number) => {
        const properties = feature.getProperties() as IceLayerFeature['properties'];
        const legendValue = properties.tastiDetails?.[styleProperty];
        const hasNtl = properties.ntlDetails?.source?.name === 'NTL';

        const baseStyle = new Style({
          stroke: new Stroke({
            color: 'black',
            width: 1,
          }),
          fill: new Fill({
            color: legendValue !== undefined ? colorScale(scale.toValue(legendValue)) : '#444',
          }),
          text: legendValue !== undefined ? new Text({
            text: (resolution > 3000) ? '' : '$' + Intl.NumberFormat('en', { notation: 'compact' }).format(legendValue),
          }) : undefined,
        });

        if (hasNtl && this.hatchPattern) {
          return [
            baseStyle,
            new Style({
              fill: new Fill({
                color: this.hatchPattern,
              }),
            }),
          ];
        }

        return baseStyle;

      },
    });
  }
}
